/* The API client. Shared, because the desk reads the same endpoints the phone
 * does — the desk is a projection reader with a live layer on top, not a
 * different backend.
 *
 * The asymmetry between the two surfaces lives at the call site, not here:
 *   · the phone treats every failure as "try again later" and never surfaces
 *     an error, because it has a queue;
 *   · the desk treats a failed write as a visible retry toast, because it
 *     doesn't.
 * So nothing in this file throws for the caller to interpret. Failures return
 * null or false.
 */

import type {
  Comment,
  FieldAssignment,
  PriorityItem,
  PullResponse,
  PushRequest,
  PushResponse,
  Uuid,
} from './types';

/** Cell edge is slow, not free. */
const TIMEOUT_MS = 12_000;

/* The desk has no outbox, but push is keyed on (device_id, client_seq), so it
   needs both anyway. The id persists per browser so a reload doesn't start
   replaying sequence numbers the server has already seen. */
const DESK_DEVICE_KEY = 'kilroy.desk.device';
const DESK_SEQ_KEY = 'kilroy.desk.seq';

function deskDeviceId(): string {
  try {
    let id = localStorage.getItem(DESK_DEVICE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(DESK_DEVICE_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

function nextSeq(): number {
  try {
    const n = Number(localStorage.getItem(DESK_SEQ_KEY) ?? '0') + 1;
    localStorage.setItem(DESK_SEQ_KEY, String(n));
    return n;
  } catch {
    return Date.now();
  }
}

export interface ApiConfig {
  base: string;
  /** Pilot-only shared secret. See the note in the fetch wrapper. */
  apiKey?: string;
  /**
   * Returns the current access token, or null.
   *
   * A getter rather than a value because a token expires and a long-lived
   * client would otherwise hold a stale one all day. Returning null is fine and
   * expected — sync then falls back to the shared key, which is what a device
   * that has not signed in yet does.
   */
  accessToken?: () => string | null;
  /** Magic-link session token. Cached for offline on the phone; capture is
   *  never blocked by expiry — the queue holds and re-auths on sync. */
  token?: string;
}

export function createApi(config: ApiConfig) {
  const { base } = config;
  let token = config.token;

  async function req(path: string, init?: RequestInit): Promise<Response | null> {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), TIMEOUT_MS);
    try {
      return await fetch(`${base}${path}`, {
        ...init,
        signal: ctl.signal,
        headers: {
          ...(init?.body ? { 'content-type': 'application/json' } : {}),
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          // Pilot gate on the deployed API — a shared secret, not auth. It
          // stops drive-by writes; it does not identify anyone. Removed the
          // moment magic-link lands. See netlify/functions/sync.mts.
          ...(config.apiKey ? { 'x-kilroy-key': config.apiKey } : {}),
          // Sent alongside the shared key, not instead of it. The server
          // prefers the token when it can verify one and falls back otherwise,
          // so a phone mid-upgrade keeps syncing rather than silently
          // accumulating a day of unsent evidence.
          ...(config.accessToken?.() ? { authorization: `Bearer ${config.accessToken()}` } : {}),
          ...init?.headers,
        },
      });
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  async function json<T>(path: string, init?: RequestInit): Promise<T | null> {
    const res = await req(path, init);
    if (!res || !res.ok) return null;
    return (await res.json()) as T;
  }

  return {
    setToken(next: string | undefined) {
      token = next;
    },

    /**
     * The connectivity probe. iOS reports `online` when it has an interface,
     * not when it has a route — a truck on a ridge with one bar of nothing is
     * "online" as far as the event is concerned. A 204 from our own origin is
     * the only answer worth trusting.
     */
    async probe(): Promise<boolean> {
      const res = await req('/health', { method: 'GET', cache: 'no-store' });
      return !!res && res.ok;
    },

    push: (body: PushRequest) => json<PushResponse>('/sync/push', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

    pull: (cursor: number, deviceId: string) =>
      json<PullResponse>(`/sync/pull?cursor=${cursor}&device_id=${encodeURIComponent(deviceId)}`),

    /* ── Media lane ─────────────────────────────────────────────────────── */

    mediaIntent: (media_id: Uuid, bytes: number, mime: string) =>
      json<{ url: string; method: 'PUT' | 'POST' }>('/media/intent', {
        method: 'POST',
        body: JSON.stringify({ media_id, bytes, mime }),
      }),

    async uploadMedia(url: string, method: 'PUT' | 'POST', file: Blob): Promise<boolean> {
      const res = await req(url.startsWith('http') ? url.replace(base, '') : url, {
        method,
        body: file,
        headers: { 'content-type': file.type || 'application/octet-stream' },
      });
      return !!res && res.ok;
    },

    /** The server HEADs the object before answering true. Local media is not
     *  pruned until it does — the phone holds the only copy until then. */
    async confirmMedia(media_id: Uuid): Promise<boolean> {
      const body = await json<{ received: boolean }>('/media/confirm', {
        method: 'POST',
        body: JSON.stringify({ media_id }),
      });
      return body?.received ?? false;
    },

    /* ── Desk writes. Five kinds of intent, per Addendum A1.
     *
     * They go through /sync/push, the same endpoint the phone uses, rather
     * than a REST route each. Three reasons, all of which bit us before this
     * was unified:
     *   · one write path means one place where a record can be wrong
     *   · (device_id, client_seq) idempotency comes free, so a double-click on
     *     Create cannot produce two assignments
     *   · the desk and the phone stay provably in sync about record shape,
     *     because there is only one shape
     *
     * The desk still has no queue — a failure here surfaces as a retry toast.
     * That asymmetry is the point; it just doesn't need a second protocol. */
    async pushOne(kind: string, record: unknown): Promise<boolean> {
      const res = await json<PushResponse>('/sync/push', {
        method: 'POST',
        body: JSON.stringify({
          device_id: deskDeviceId(),
          device_sent_at: new Date().toISOString(),
          entries: [{ client_seq: nextSeq(), kind, record }],
        }),
      });
      if (!res) return false;
      if (res.rejected.length) {
        console.warn('[kilroy] push rejected:', res.rejected[0].reason);
        return false;
      }
      return res.acked_seqs.length > 0;
    },

    createAssignment(a: FieldAssignment) { return this.pushOne('assignment', a); },
    createPriorityItem(p: PriorityItem) { return this.pushOne('priority_item', p); },
    createComment(c: Comment) { return this.pushOne('comment', c); },
    /** Server-side RLS decides whether this is allowed, not the client. */
    rectify(record: unknown) { return this.pushOne('rectified', record); },
    /** Confirm / dispute / needs-more-info. An appended row, never an edit. */
    changeStatus(record: unknown) { return this.pushOne('status', record); },
    /** A correction is a new row that supersedes on read. The original stays. */
    correct(record: unknown) { return this.pushOne('correction', record); },

    metrics: () => json<Record<string, unknown>>('/metrics/first-light'),

    /** SSE if the stack makes it trivial; the caller falls back to polling.
     *  v1 requirement is "feels live during the demo", not websockets purity. */
    events(onMessage: (data: unknown) => void): () => void {
      let source: EventSource | null = null;
      try {
        source = new EventSource(`${base}/events`);
        source.onmessage = (e) => {
          try {
            onMessage(JSON.parse(e.data));
          } catch {
            /* malformed frame — the next pull reconciles anyway */
          }
        };
      } catch {
        return () => {};
      }
      return () => source?.close();
    },
  };
}

export type Api = ReturnType<typeof createApi>;

/**
 * URL for a media object, for use directly in an `<img src>`.
 *
 * The endpoint 302s to a short-lived signed URL, so the storage bucket stays
 * private — evidence photographs carry claim locations and unannounced rock,
 * which is commercially sensitive for a company mid-campaign.
 *
 * No auth header, because an image tag cannot send one. Security is that the
 * media id is an unguessable UUID and the endpoint signs nothing else. See the
 * note beside the route in sync.mts for the honest limits of that.
 */
export const mediaUrl = (base: string, mediaId: string): string =>
  `${base.replace(/\/$/, '')}/media/view?id=${encodeURIComponent(mediaId)}`;
